/** Closed, DOM-free controls shared by coach consumer transports and keys. */
export type CoachingConsumerPage = { limit?: number; cursor?: string | null };
export type CoachingConsumerSelection =
  | { kind: 'goal-support'; goalId: string; playerId: string }
  | { kind: 'history'; playerId: string }
  | { kind: 'mixed-history'; playerId: string }
  | { kind: 'candidates'; playerId: string; candidateKind: 'action' | 'focus' | 'support' };

function closed(value: unknown, keys: readonly string[]): asserts value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || Object.keys(value).some(key => !keys.includes(key))) throw new RangeError('invalid_consumer_query');
}

export function normalizeCoachingConsumerQuery(selection: CoachingConsumerSelection, controls: CoachingConsumerPage = {}) {
  closed(selection, ['kind', 'playerId', ...(selection?.kind === 'goal-support' ? ['goalId']
    : selection?.kind === 'candidates' ? ['candidateKind'] : [])]);
  if (!['goal-support', 'history', 'mixed-history', 'candidates'].includes(selection.kind)
    || typeof selection.playerId !== 'string' || [...selection.playerId].length < 1
    || [...selection.playerId].length > 200) throw new RangeError('invalid_consumer_query');
  let target: CoachingConsumerSelection;
  if (selection.kind === 'goal-support') {
    if (typeof selection.goalId !== 'string' || !/^[1-9][0-9]{0,18}$/.test(selection.goalId)
      || BigInt(selection.goalId) > 9223372036854775807n) throw new RangeError('invalid_consumer_query');
    target = { kind: selection.kind, playerId: selection.playerId, goalId: selection.goalId };
  } else if (selection.kind === 'candidates') {
    if (!['action', 'focus', 'support'].includes(selection.candidateKind)) throw new RangeError('invalid_consumer_query');
    target = { kind: selection.kind, playerId: selection.playerId, candidateKind: selection.candidateKind };
  } else target = { kind: selection.kind, playerId: selection.playerId };
  closed(controls, ['limit', 'cursor']);
  const limit = controls.limit === undefined ? 20 : controls.limit;
  const cursor = controls.cursor ?? null;
  if (!Number.isSafeInteger(limit) || limit < 1 || limit > 50
    || (cursor !== null && (typeof cursor !== 'string' || [...cursor].length < 1 || [...cursor].length > 128))) {
    throw new RangeError('invalid_consumer_query');
  }
  return { target, limit, cursor };
}

export function coachingConsumerRequest(selection: CoachingConsumerSelection, controls: CoachingConsumerPage = {}) {
  const { target, limit, cursor } = normalizeCoachingConsumerQuery(selection, controls);
  const query: Record<string, string> = { limit: String(limit), ...(cursor === null ? {} : { cursor }) };
  if (target.kind === 'goal-support') {
    return { path: `/api/coach/goals/${target.goalId}/feedback-support`, query: { ...query, player_id: target.playerId } };
  }
  const base = `/api/coach/players/${encodeURIComponent(target.playerId)}`;
  if (target.kind === 'mixed-history') return { path: `${base}/development-history`, query };
  return target.kind === 'history' ? { path: `${base}/canonical-development`, query }
    : { path: `${base}/canonical-followup-candidates`, query: { ...query, kind: target.candidateKind } };
}

/** Exact four read shapes; never a prefix-based capability grant. */
export function isCoachingConsumerPath(path: string): boolean {
  return /^\/api\/coach\/goals\/[1-9][0-9]{0,18}\/feedback-support$/.test(path)
    || /^\/api\/coach\/players\/[^/]+\/development-history$/.test(path)
    || /^\/api\/coach\/players\/[^/]+\/canonical-(development|followup-candidates)$/.test(path);
}
